import { differenceInSeconds } from 'date-fns'

// The disagreement context recorded when a scheduler books a slot the AI did
// not suggest. Not a verdict on the model — the scheduler may have out-of-band
// context — but a comparison of the human's choice against the model's
// candidates, so tuning can tell "ranked the wrong slot" (near-miss, inside the
// suggested window) apart from "never had the right candidate" (far off,
// outside the window, e.g. a visit booked months out beyond the search window).
export interface SuggestionDisagreement {
  // Whether the booked start falls within [earliest, latest] of the suggested
  // starts. False when the model never proposed anything near this date.
  bookedWithinSuggestedWindow: boolean
  // Absolute seconds between the booked start and the nearest suggested start;
  // null when there were no suggestions to compare against.
  secondsFromNearestSuggestion: number | null
}

export function computeSuggestionDisagreement(
  bookedStart: string,
  suggestedStarts: string[]
): SuggestionDisagreement {
  if (suggestedStarts.length === 0) {
    return {
      bookedWithinSuggestedWindow: false,
      secondsFromNearestSuggestion: null,
    }
  }

  const booked = new Date(bookedStart).getTime()
  const times = suggestedStarts.map((s) => new Date(s).getTime())
  const earliest = Math.min(...times)
  const latest = Math.max(...times)

  const secondsFromNearestSuggestion = Math.min(
    ...suggestedStarts.map((s) =>
      Math.abs(differenceInSeconds(booked, new Date(s)))
    )
  )

  return {
    bookedWithinSuggestedWindow: booked >= earliest && booked <= latest,
    secondsFromNearestSuggestion,
  }
}
